import { useMemo } from 'react'
import CONFIG from '../config'

const DEG2RAD = Math.PI / 180

function haversine(a, b) {
  const dLat = DEG2RAD * (b[1] - a[1])
  const dLon = DEG2RAD * (b[0] - a[0])
  const sinLat = Math.sin(dLat / 2)
  const sinLon = Math.sin(dLon / 2)
  const h = sinLat * sinLat + Math.cos(DEG2RAD * a[1]) * Math.cos(DEG2RAD * b[1]) * sinLon * sinLon
  return 6371000 * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h))
}

function buildProfile(geojson) {
  if (!geojson) return null
  let data = typeof geojson === 'string' ? JSON.parse(geojson) : geojson
  let coords
  if (data.features) {
    const feat = data.features.find(f => f.geometry && f.geometry.type === 'LineString')
    coords = feat ? feat.geometry.coordinates : null
  } else if (data.coordinates) {
    coords = data.coordinates
  } else if (Array.isArray(data) && data.length && Array.isArray(data[0])) {
    coords = data
  }
  if (!coords || coords.length < 2 || coords[0].length < 3) return null
  let dist = 0
  const points = [{ d: 0, ele: coords[0][2] }]
  for (let i = 1; i < coords.length; i++) {
    dist += haversine(coords[i - 1], coords[i])
    points.push({ d: dist, ele: coords[i][2] })
  }
  return points
}

function ElevationProfile({ competicion, resultados, controles }) {
  const profile = useMemo(() => buildProfile(competicion?.mapa_geojson), [competicion?.mapa_geojson])

  if (!profile || profile.length < 2) return null

  const totalDist = profile[profile.length - 1].d
  const eles = profile.map(p => p.ele)
  const minEle = Math.floor(Math.min(...eles))
  const maxEle = Math.ceil(Math.max(...eles))
  const eleRange = maxEle - minEle || 1

  const W = 800
  const H = 280
  const PAD = { top: 24, right: 55, bottom: 34, left: 55 }
  const gW = W - PAD.left - PAD.right
  const gH = H - PAD.top - PAD.bottom

  const toX = d => PAD.left + (d / totalDist) * gW
  const toY = ele => PAD.top + gH - ((ele - minEle) / eleRange) * gH
  const eleAt = d => {
    for (let i = 1; i < profile.length; i++) {
      if (profile[i].d >= d) {
        const t = (d - profile[i-1].d) / (profile[i].d - profile[i-1].d || 1)
        return profile[i-1].ele + t * (profile[i].ele - profile[i-1].ele)
      }
    }
    return profile[profile.length - 1].ele
  }

  const linePath = profile.map((p, i) =>
    `${i === 0 ? 'M' : 'L'}${toX(p.d).toFixed(1)},${toY(p.ele).toFixed(1)}`
  ).join(' ')
  const areaPath = linePath +
    ` L${toX(totalDist).toFixed(1)},${(PAD.top + gH).toFixed(1)}` +
    ` L${PAD.left},${(PAD.top + gH).toFixed(1)} Z`

  const distKm = totalDist / 1000
  const ticks = []
  const step = distKm <= 5 ? 1 : distKm <= 20 ? 2 : distKm <= 50 ? 5 : 10
  for (let k = 0; k <= distKm; k += step) ticks.push(k)
  if (ticks[ticks.length - 1] < distKm - step * 0.3) ticks.push(Math.round(distKm * 10) / 10)
  ticks[ticks.length - 1] = ticks[ticks.length - 1]  // last tick gets km suffix below

  const eleTicks = []
  const eleStep = eleRange <= 200 ? 50 : eleRange <= 500 ? 100 : eleRange <= 1500 ? 250 : 500
  for (let e = Math.ceil(minEle / eleStep) * eleStep; e <= maxEle; e += eleStep) eleTicks.push(e)

  const dPlus = profile.reduce((acc, p, i) => i > 0 && p.ele > profile[i-1].ele ? acc + (p.ele - profile[i-1].ele) : acc, 0)

  const metaControl = (controles || []).find(c => c.tipo === 'META')
  const distMeta = metaControl ? metaControl.distancia : totalDist
  const controlMarks = (controles || [])
    .filter(c => c.tipo !== 'SALIDA' && c.distancia > 0)
    .map(c => {
      const d = (c.distancia / distMeta) * totalDist
      const ele = eleAt(d)
      return { nombre: c.nombre, tipo: c.tipo, x: toX(d), y: toY(ele), yBottom: PAD.top + gH }
    })

  const BRAND = '#dc2626'

  const colors = CONFIG.COLORES_FAVORITOS || []
  const athletes = (resultados || [])
    .map((r, i) => {
      let pct = r.prevision ? r.prevision.porcentaje : null
      if (!pct && r.prevision && r.prevision.controlUltimo && r.prevision.controlUltimo.tipo === 'META') pct = 100
      if (!pct || pct <= 0) return null
      const d = (Math.min(pct, 100) / 100) * totalDist
      const ele = eleAt(d)
      const initials = ((r.nombre ? r.nombre[0] : '') + (r.apellidos ? r.apellidos[0] : '')).toUpperCase() || String(r.dorsal || '')
      return { key: r.id || i, x: toX(d), y: toY(ele), color: colors[i % colors.length] || BRAND, initials }
    })
    .filter(Boolean)

  return (
    <div className="bg-gray-900 border-t border-gray-700">
      <div className="mx-auto max-w-5xl">
      <div className="flex items-center justify-between px-4 pt-4 pb-2">
        <span className="text-sm font-semibold text-white uppercase tracking-wide">Altimetría</span>
        <span className="text-sm text-white">{distKm.toFixed(1)} km · D+{Math.round(dPlus)} m</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full block" preserveAspectRatio="xMidYMid meet">
        <defs>
          <linearGradient id="eleGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={BRAND} stopOpacity="0.25" />
            <stop offset="100%" stopColor={BRAND} stopOpacity="0.03" />
          </linearGradient>
        </defs>
        {eleTicks.map(e => (
          <g key={e}>
            <line x1={PAD.left} y1={toY(e)} x2={W - PAD.right} y2={toY(e)} stroke="#374151" strokeWidth="0.5" />
            <text x={PAD.left - 4} y={toY(e) + 3} textAnchor="end" fontSize="15" fill="#d1d5db">{e}m</text>
          </g>
        ))}
        {ticks.map(k => (
          <text key={k} x={toX(k * 1000)} y={H - 5} textAnchor="middle" fontSize="15" fill="#d1d5db">{k === ticks[ticks.length - 1] ? k + " km" : k}</text>
        ))}
        
        <path d={areaPath} fill="url(#eleGrad)" />
        <path d={linePath} fill="none" stroke={BRAND} strokeWidth="3" strokeLinejoin="round" />
        {controlMarks.map((c, i) => (
          <g key={'ctrl-' + i}>
            <line x1={c.x} y1={c.y} x2={c.x} y2={c.yBottom} stroke="#d1d5db" strokeWidth="3" strokeDasharray="5,3" />
            <circle cx={c.x} cy={c.y} r="6" fill="#d1d5db" stroke="#1f2937" strokeWidth="1" />
            <text x={c.x} y={c.yBottom + 14} textAnchor="middle" fontSize="16" fill="#d1d5db" fontWeight="500">{c.tipo === 'META' ? 'META' : c.nombre}</text>
          </g>
        ))}
        {athletes.map(a => (
          <g key={a.key}>
            <line x1={a.x} y1={a.y + 6} x2={a.x} y2={PAD.top + gH} stroke={a.color} strokeWidth="1" opacity="0.4" strokeDasharray="3,2" />
            <circle cx={a.x} cy={a.y} r="14" fill={a.color} stroke="#111827" strokeWidth="2" />
            <text x={a.x} y={a.y + 5} textAnchor="middle" fontSize="13" fontWeight="bold" fill="#fff">{a.initials}</text>
          </g>
        ))}
      </svg>
      </div>
    </div>
  )
}

export default ElevationProfile
